import React, { useEffect, useState, useCallback, useMemo } from 'react'

import { deleteImage, getHostGlanceEndpoint, testHostGlance } from './actions'
import { deleteVolumeSnapshot } from 'app/plugins/openstack/components/storage/snapshots/actions'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import ExternalLink from 'core/components/ExternalLink'
import { routeManager } from 'core/utils/routes'
import { IHealthServiceDetailsPageTabs } from '../networks/model'
import Modal from 'core/elements/modal'
import Button from 'core/elements/button'
import Text from 'core/elements/Text'
import Checkbox from 'core/elements/input/Checkbox'
import { listVolumeSnapshots } from 'app/plugins/openstack/components/storage/snapshots/actions'
import { volumeSnapshotsByIdSelector } from 'app/plugins/openstack/components/storage/snapshots/selectors'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import useDeleteAction from 'core/hooks/useDeleteAction'
import ShowActionState, { ActionStateResult } from 'core/components/ShowActionState'
import Info from 'core/components/validatedForm/Info'
import { initializeResults, processResults } from 'app/plugins/openstack/components/images/helpers'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Grid from 'core/elements/grid'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Tooltip from 'core/elements/tooltip'
import clsx from 'clsx'

interface DeleteImageProps {
  rows: any[]
  onClose: (value: boolean) => void
}

const ImageServiceStatusMessage = ({ needToValidateCert, glanceHostNotReachable }) => {
  if (needToValidateCert) {
    return (
      <Info error>
        <ErrorMessage>Trust the image library certificate before proceeding.</ErrorMessage>
      </Info>
    )
  }

  if (glanceHostNotReachable) {
    return (
      <Info error>
        <ErrorMessage>
          <Text variant="body2">
            Image library host not reachable. Please check the image service health{' '}
            <ExternalLink
              url={routeManager.routes.openstack.health.details.path({
                tab: IHealthServiceDetailsPageTabs.ServiceHealth,
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </ExternalLink>
            .
          </Text>
        </ErrorMessage>
      </Info>
    )
  }

  return null
}
const pageSizeOptions = [3, 5, 10].map((value) => ({ value }))

export default function DeleteVmSnapshot({ rows, onClose }: DeleteImageProps) {
  const classes = useStyles()
  const [showWarning, setShowWarning] = useState(false)
  const [needToValidateCert, setNeedToValidateCert] = useState(false)
  const [glanceHostNotReachable, setGlanceHostNotReachable] = useState(false)
  const [isDeleteAssociatedVolSnapshots, setIsDeleteAssociatedVolSnapshots] = useState(true)

  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector)

  const hasSnapshots = useMemo(() => {
    return rows?.some((row) => row?.snapshotIds?.length > 0)
  }, [rows])

  // Get all associated volume snapshots for all selected images using volumeSnapshotsById
  const associatedVolumeSnapshots = useMemo(() => {
    return rows
      ?.flatMap((image) => image?.snapshotIds) // get all snapshotIds for all selected images
      ?.map((snapshotId) => volumeSnapshotsById?.[snapshotId])
      ?.filter(Boolean) //filter out null values
  }, [rows, volumeSnapshotsById])

  // Initialize image results with names
  const [imageResults, setImageResults] = useState<ActionStateResult[]>(
    initializeResults(rows, 'Deleting snapshot', null),
  )

  // Initialize volume snapshot results with names from volumeSnapshotsById
  const [volumeSnapshotResults, setVolumeSnapshotResults] = useState<ActionStateResult[]>(
    initializeResults(associatedVolumeSnapshots, 'Deleting Volume Snapshot', null),
  )

  // Use separate deletion hooks for images and volume snapshots
  const { bulkRemove: bulkRemoveImages } = useDeleteAction(deleteImage)
  const { bulkRemove: bulkRemoveVolumeSnapshots } = useDeleteAction(deleteVolumeSnapshot)

  useEffect(() => {
    const asyncCall = async () => {
      try {
        const endpoint = await getHostGlanceEndpoint()
        if (!endpoint) {
          setShowWarning(true)
        }
        await testHostGlance(endpoint)
      } catch (err) {
        if (err?.err?.message === 'Network Error') {
          setNeedToValidateCert(true)
        } else if (err?.err?.code === 'ECONNABORTED') {
          setGlanceHostNotReachable(true)
        }
      }
    }
    asyncCall()
  }, [])

  // Handle delete operation
  const handleDelete = useCallback(async () => {
    // Initialize image deletion
    const initialImageResults = initializeResults(rows, 'Deleting snapshot')
    setImageResults(initialImageResults)

    // Delete images first
    const imageRowsWithBody = rows.map((image) => ({
      id: image?.id,
      queued: false,
    }))

    const imageResults = await bulkRemoveImages(imageRowsWithBody)
    const processedImageResults = processResults('Snapshot', imageResults, rows)
    setImageResults(processedImageResults)

    //Skip the volume snpshot deletion if,
    // 1. User has chosen to not delete associated volume snapshots
    // 2. Any image deletion failed
    // 3. There are no associated volume snapshots
    const hasFailedImage = processedImageResults?.some((res) => res?.status === 'Failed')
    if (hasFailedImage || !isDeleteAssociatedVolSnapshots || !associatedVolumeSnapshots?.length)
      return

    // Initialize volume snapshot deletion with In Progress status
    const initialVolumeSnapshotResults = initializeResults(
      associatedVolumeSnapshots,
      'Deleting Volume Snapshot',
    )
    setVolumeSnapshotResults(initialVolumeSnapshotResults)

    const volumeSnapshotPayload = associatedVolumeSnapshots?.map((vs) => ({ id: vs?.id }))
    const volumeSnapshotResults = await bulkRemoveVolumeSnapshots(volumeSnapshotPayload)
    const processedVolumeSnapshotResults = processResults(
      'Volume Snapshot',
      volumeSnapshotResults,
      associatedVolumeSnapshots,
    )
    setVolumeSnapshotResults(processedVolumeSnapshotResults)
  }, [
    rows,
    bulkRemoveImages,
    bulkRemoveVolumeSnapshots,
    associatedVolumeSnapshots,
    isDeleteAssociatedVolSnapshots,
    initializeResults,
    processResults,
  ])

  const handleClose = useCallback(() => {
    setImageResults([])
    setVolumeSnapshotResults([])
    onClose(true)
  }, [onClose])

  const showFinishButton = useMemo(
    () => imageResults?.some((r) => !!r?.status) || volumeSnapshotResults?.some((r) => !!r?.status),
    [imageResults, volumeSnapshotResults],
  )

  const isAnyVolumeSnapshotDeleteFailed = useMemo(
    () => volumeSnapshotResults?.some((r) => r?.status === 'Failed'),
    [volumeSnapshotResults],
  )

  const data = useMemo(
    () =>
      volumeSnapshotResults?.map((vs) => {
        // Find the row that has this snapshot
        const row = rows?.find((r) => r?.snapshotIds?.some((id) => id === vs?.id))
        return {
          ...vs,
          id: vs.id,
          vmSnapshotName: row?.name || '',
        }
      }),
    [rows, volumeSnapshotResults],
  )

  const columns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Volume Snapshot',
      },
      ...(rows?.length > 1 ? [{ key: 'vmSnapshotName', label: 'VM Snapshot' }] : []),
      {
        key: 'status',
        label: 'Status',
        CellComponent: ({ item }) => {
          const className = item?.status === 'Failed' ? classes.failedIcon : classes.successIcon
          const actionInProgress = item?.status === 'In Progress'
          const actionFailed = item?.status === 'Failed'

          if (!item?.status) return null

          return (
            <Tooltip message={actionFailed && item?.error ? item?.error : ''}>
              <Text variant="body2">
                <FontAwesomeIcon className={className} spin={actionInProgress}>
                  {actionInProgress
                    ? 'rotate'
                    : actionFailed
                    ? 'circle-exclamation'
                    : 'circle-check'}
                </FontAwesomeIcon>
                {actionInProgress ? 'Deleting' : actionFailed ? 'Failed' : 'Deleted'}
              </Text>
            </Tooltip>
          )
        },
      },
    ],
    [rows, classes],
  )

  return (
    <Modal
      panel="dialog"
      open
      onClose={handleClose}
      title="Delete Snapshot(s)"
      maxWidth={hasSnapshots ? 800 : 600}
      footer={
        showFinishButton ? (
          <Button variant="secondary" onClick={handleClose}>
            Finish
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleDelete}>Confirm</Button>
          </>
        )
      }
    >
      <>
        <ImageServiceStatusMessage
          needToValidateCert={needToValidateCert}
          glanceHostNotReachable={glanceHostNotReachable}
        />
        {/* Images Section */}
        <ShowActionState
          title={
            showWarning
              ? 'You are about to delete snapshot(s) that belong to a host without an Image Library role. This action cannot be undone.'
              : 'This will permanently delete the following snapshot(s):'
          }
          results={imageResults}
          customClassName={classes.showImagesActionState}
        />
        {isAnyVolumeSnapshotDeleteFailed && (
          <Info error>
            <ErrorMessage className={classes.errorMessageContainer}>
              <Text variant="body2">
                Failed to cleanup the volume snapshot. You may need to delete the volume snapshot
                manually.{' '}
                <ExternalLink url={routeManager.routes.openstack.volumeSnapshots.path()}>
                  Click here
                </ExternalLink>
              </Text>
            </ErrorMessage>
          </Info>
        )}
        {hasSnapshots && (
          <>
            <Info className={clsx(classes.box, classes.warning)} error>
              {isDeleteAssociatedVolSnapshots
                ? 'The associated volume snapshot(s) for the selected VM snapshot(s) will be deleted as part of this operation. Uncheck the checkbox if you wish to retain them.'
                : 'The associated volume snapshot(s) for the selected VM snapshot(s) will be retained.'}
            </Info>
            <Checkbox
              label="Delete all the associated volume snapshots"
              checked={isDeleteAssociatedVolSnapshots}
              onChange={() => setIsDeleteAssociatedVolSnapshots(!isDeleteAssociatedVolSnapshots)}
              id="deleteAllVolumeSnapshots"
            />
            {isDeleteAssociatedVolSnapshots && (
              <>
                <div className={classes.grid}>
                  <Grid
                    uniqueIdentifier="id"
                    columns={columns}
                    data={data}
                    label="Volume Snapshots"
                    orderBy="name"
                    orderDirection="asc"
                    emptyContent="No associated volume snapshots found"
                    disableRowSelection={true}
                    disableColumnHiding
                    rowsPerPage={3}
                    pageSizeOptions={pageSizeOptions}
                  />
                </div>
              </>
            )}
          </>
        )}
      </>
    </Modal>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  errorMessageContainer: {
    gap: theme.spacing(1),
  },
  showActionState: {
    display: 'grid',
    gridTemplateColumns: '250px max-content',
  },
  showImagesActionState: {
    display: 'grid',
    gridTemplateColumns: '8px 1fr 1fr',
  },
  grid: {
    marginBottom: theme.spacing(2),
  },
  failedIcon: {
    color: theme.components.badge.error.color,
    marginRight: 8,
  },
  successIcon: {
    color: theme.components.badge.success.color,
    marginRight: 8,
  },
  warning: {
    border: `1px solid ${theme.palette.yellow[500]}`,
    background: theme.palette.yellow[100],
  },
}))
